import create from 'zustand';
import produce from 'immer';

// internal
import * as ZoneStore from '@typings/stores/initializing/zone';
import * as ZoneState from '@typings/stores/states/zone';
import * as ZoneService from '@service/zone';
import { handleError } from '@app/utils/service';
import { getBrand, getProject, getZone, setZone } from '@app/utils/localStorage';
import { DefaultListDataState } from '@app/typings/stores/states/default.d';
import notification from 'antd/lib/notification';
import { useAssetDeviceStore } from '@store/project/assetDevice';

const state: ZoneState.ZoneState = {
  selected: { id: '', name: '' },
  projectSelected: { id: '', name: '' },
  lists: [],
  modalDelete: false,
};

export const useZoneStore = create<ZoneStore.ZoneStore>((set, get) => ({
  ...state,
  onSelected: async (id, checkSelected) => {
    if (checkSelected && id === get().selected.id) {
      return;
    }

    try {
      const { data } = await ZoneService.fetchZoneWithId(id);
      if (data.success) {
        set(
          produce<ZoneState.ZoneState>((state) => {
            state.selected = {
              id: data.data._id,
              name: data.data.name,
            };
          }),
        );
      }
    } catch (error) {
      handleError(error);
    }
  },
  onProjectSelected: async (id: string) => {
    if (!id) {
      id = getZone();
    } else if (id === get().projectSelected.id) {
      return;
    }

    try {
      const { data } = await ZoneService.fetchZoneWithId(id);
      if (data.success) {
        setZone(id);
        set(
          produce<ZoneState.ZoneState>((state) => {
            state.projectSelected = {
              id: data.data._id,
              name: data.data.name,
            };
          }),
        );
      }
    } catch (error) {
      handleError(error);
    }
  },
  onList: async (option) => {
    try {
      const { data } = await ZoneService.fetchZoneSelect(getBrand(), getProject());
      if (data.success) {
        const transformData = data.data.map<DefaultListDataState>((value) => {
          return {
            label: value.name || '-',
            value: value._id,
          };
        });

        // auto select when empty zone
        let current: any;
        if (option?.autoProjectSelect && transformData.length > 0) {
          current = transformData[0];
          if (current) {
            setZone(current.value);
            await useAssetDeviceStore.getState().fetch(current.value);
          }
        }

        set(
          produce<ZoneState.ZoneState>((state) => {
            state.lists = transformData;

            if (!current) {
              const newTransformData = transformData.filter((data) => data.value === getZone());
              current = newTransformData[0];
              state.projectSelected = { name: current.label, id: current.value };
              useAssetDeviceStore.getState().fetch(current.value);
            } else {
              state.projectSelected = { name: current.label, id: current.value };
              useAssetDeviceStore.getState().fetch(current.value);
            }
          }),
        );
      }
    } catch (error) {
      handleError(error);
    }
  },
  onDelete: async () => {
    try {
      const selected = get().projectSelected;
      const lists = get().lists;
      const { status } = await ZoneService.fetchZoneDelete(selected.id);
      if (status === 204) {
        notification.success({
          message: 'Alert',
          description: `Delete zone "${selected.name}" success`,
        });

        set(
          produce<ZoneState.ZoneState>((state) => {
            const currentLists = lists.filter((f) => {
              return f.value !== selected.id;
            });
            if (currentLists.length > 0) {
              const value = currentLists[0];
              state.projectSelected = { name: value.label, id: value.value };
            } else {
              state.projectSelected = { name: '', id: '' };
            }

            state.lists = currentLists;
          }),
        );

        get().onList();
      }
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error.response.data.message,
      });
    }
  },
  onModalDelete: () => {
    set(
      produce<ZoneState.ZoneState>((state) => {
        state.modalDelete = !state.modalDelete;
      }),
    );
  },
}));
